import { useState } from "react";
import styled from "styled-components";

import Dialog from "src/components/core/Dialog";
import Button from "src/components/core/Button";
import {
  Grid,
  Stack,
  Box,
  IconButton,
  FormHelperText,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CameraIcon from "src/components/icon/Camera";

import { DialogTitle, DialogContent, DialogActions } from "@mui/material";

import font from "src/styles/font";

import { createRoom, updateRoom } from "src/api/shopThisMood";
import { useDialog } from "src/context/DialogContext";
import { useSnackbar } from "notistack";

const S = {};

S.PreviewImageUploadBox = styled.div`
  position: relative;
  padding-top: 100%;
  background-color: #f3f1f2;
  border-radius: 24px;
  /* grid-column: span 7; */
`;

S.UploadInnerBox = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

S.UploadInput = styled.label`
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
`;

S.UploadLabel = styled.span`
  font-size: ${font.size.md};
  font-weight: bold;
  color: #6c6c70;
  margin-top: 16px;

  &::after {
    content: "*";
    font-weight: 600;
    color: #e04132;
  }
`;

S.UploadHelperText = styled.span`
  display: flex;
  justify-content: center;
  font-size: ${font.size.sm};
  color: #6c6c70;
  margin-top: 8px;
`;

S.UploadedPreviewImage = styled.img`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  border-radius: 15px;
  object-fit: cover;
`;

const AddRoomDialog = ({ initialValue, onClose, onSuccess, editable }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { openDialog } = useDialog();

  const [formData, setFormData] = useState(
    initialValue ?? {
      cover_image: null,
      room_th: "",
      room_en: "",
      room_cn: "",
    }
  );

  const isFormValid = formData?.cover_image && formData?.room_th;

  const handleSubmit = async () => {
    try {
      const payload = {
        ...formData,
      };

      const data = new FormData();
      for (const field in payload) {
        data.append(field, payload[field]);
      }

      !editable ? await createRoom(data) : await updateRoom(payload._id, data);

      enqueueSnackbar(`${editable ? "บันทึก" : "เพิ่มห้อง"}สำเร็จ`, {
        variant: "success",
      });

      onSuccess();
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <Dialog
      onClose={onClose}
      aria-labelledby="customized-dialog-title"
      open={true}
      sx={{
        "& .MuiDialog-container": {
          "& .MuiPaper-root": {
            width: 1024,
            maxWidth: "100%",
          },
        },
        "& .MuiDialogContent-root": {
          pt: 1,
          pb: 4,
        },
      }}
    >
      <DialogTitle sx={{ p: 3 }}>
        {editable ? "แก้ไข" : "เพิ่ม"}ห้อง
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container>
          <Grid item md={5}>
            <S.PreviewImageUploadBox>
              {!formData?.cover_image ? (
                <S.UploadInnerBox>
                  <Stack flexDirection={"row"}>
                    <input
                      id="previewImgFieldHandle"
                      type="file"
                      accept=".jpg,.jpeg,.png"
                      style={{ display: "none" }}
                      onChange={(e) => {
                        e.preventDefault();
                        setFormData({
                          ...formData,
                          cover_image: e.target.files[0],
                        });
                      }}
                      onClick={(e) => (e.currentTarget.value = null)}
                    />
                    <S.UploadInput htmlFor="previewImgFieldHandle">
                      <CameraIcon size={60} color="#BCBCC0" />
                      <S.UploadLabel>อัปโหลดรูปภาพ</S.UploadLabel>
                      <S.UploadHelperText>
                        รูปภาพขนาด 1240 X 1240 px ขนาดไฟล์สูงสุดไม่เกิน 4 MB
                      </S.UploadHelperText>
                    </S.UploadInput>
                  </Stack>
                </S.UploadInnerBox>
              ) : (
                <>
                  <S.UploadedPreviewImage
                    src={
                      typeof formData?.cover_image === "string"
                        ? formData?.cover_image
                        : URL.createObjectURL(formData?.cover_image)
                    }
                    alt=""
                  />
                  <Stack
                    sx={{
                      position: "absolute",
                      top: 6,
                      right: 6,
                    }}
                    flexDirection={"row"}
                    columnGap={1}
                  >
                    <IconButton
                      sx={{
                        padding: "4px",
                        backgroundColor: "#BCBCC0bd",
                      }}
                      onClick={() => {
                        setFormData({
                          ...formData,
                          cover_image: null,
                        });
                      }}
                    >
                      <CloseIcon sx={{ color: "#ffffff" }} />
                    </IconButton>
                  </Stack>
                </>
              )}
            </S.PreviewImageUploadBox>
            {!formData?.cover_image && (
              <FormHelperText error={!formData?.cover_image}>
                กรุณาเลือกรูปภาพห้อง
              </FormHelperText>
            )}
          </Grid>
          <Grid item md={7}>
            <Stack
              sx={{
                pl: 3,
              }}
            >
              <label
                style={{
                  alignSelf: "flex-start",
                  fontSize: font.size.md,
                  marginBlock: 12,
                }}
              >
                ชื่อภาษาไทย
              </label>
              <TextField
                label="ชื่อภาษาไทย"
                variant="outlined"
                required
                value={formData?.room_th}
                onChange={(e) =>
                  setFormData({ ...formData, room_th: e.target.value })
                }
                fullWidth
                inputProps={{ maxLength: 50 }}
                helperText={
                  !formData?.room_th ? (
                    "กรุณากรอกชื่อห้องภาษาไทย"
                  ) : (
                    <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                      {formData?.room_th?.length}/50
                    </Box>
                  )
                }
                error={!formData?.room_th}
              />
              <label
                style={{
                  alignSelf: "flex-start",
                  fontSize: font.size.md,
                  marginBlock: 12,
                }}
              >
                ชื่อภาษาอังกฤษ
              </label>
              <TextField
                label="ชื่อภาษาอังกฤษ"
                variant="outlined"
                required
                value={formData?.room_en}
                onChange={(e) =>
                  setFormData({ ...formData, room_en: e.target.value })
                }
                fullWidth
                inputProps={{ maxLength: 50 }}
                helperText={
                  <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                    {formData?.room_en?.length}/50
                  </Box>
                }
              />
              <label
                style={{
                  alignSelf: "flex-start",
                  fontSize: font.size.md,
                  marginBlock: 12,
                }}
              >
                ชื่อภาษาจีน
              </label>
              <TextField
                label="ชื่อภาษาจีน"
                variant="outlined"
                required
                value={formData?.room_cn}
                onChange={(e) =>
                  setFormData({ ...formData, room_cn: e.target.value })
                }
                fullWidth
                inputProps={{ maxLength: 50 }}
                helperText={
                  <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                    {formData?.room_cn?.length}/50
                  </Box>
                }
              />
            </Stack>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions
        sx={{
          justifyContent: "center",
        }}
      >
        <Button
          variant="outlined"
          size="large"
          onClick={onClose}
          sx={{
            minWidth: 160,
          }}
        >
          ยกเลิก
        </Button>
        <Button
          variant="contained"
          size="large"
          onClick={() => {
            openDialog({
              type: "confirm",
              title: `คุณต้องการอัปโหลดภาพใช่หรือไม่?`,
              onConfirm: () => {
                handleSubmit();
              },
              maxWidth: 342,
            });
          }}
          sx={{
            minWidth: 160,
          }}
          // autoFocus // enable auto focus to increase accessibility
          disabled={!isFormValid}
        >
          {editable ? "บันทึก" : "อัพโหลด"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddRoomDialog;
